import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { PointsChart } from "@/components/charts/PointsChart";
import { ListChecks, BookOpen, Gift, Skull, CalendarDays, MessageCircle, ChevronDown, ChevronRight, Palmtree, Check, X, Calendar, History } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { showToast } from "@/components/shared/Toast";
import { Button } from "@/components/ui/button";

interface BatchItem {
  item_id: string;
  item_type: string;
  points: number;
  name: string;
  icon: string;
}

interface HistoryItem {
  type: string;
  points: number;
  created_at: string;
  entity_name?: string;
  entity_icon?: string;
  encrypted_note?: string;
  ref_name?: string;
  ref_icon?: string;
  streak_days?: number;
  streak_factor?: number;
  items?: BatchItem[];
}

function getEventType(type: string) {
  switch (type) {
    case "rule_broken": return "rule";
    case "chore_completed": return "chore-done";
    case "chore_overdue": return "chore-overdue";
    case "rule_streak": return "streak";
    case "session_purchase":
    case "session_purchase_reversed":
    case "reward_redeemed":
    case "punishment_redeemed":
    case "reward_reversed":
    case "punishment_reversed":
      return "session";
    default: return "other";
  }
}

function getEventStyle(type: string) {
  switch (type) {
    case "rule":
      return { emoji: "⚠️", bg: "bg-app-danger/10", border: "border-l-app-danger", text: "text-app-danger" };
    case "chore-done":
      return { emoji: "✅", bg: "bg-app-success/10", border: "border-l-app-success", text: "text-app-success" };
    case "chore-overdue":
      return { emoji: "⏰", bg: "bg-app-warning/10", border: "border-l-app-warning", text: "text-app-warning" };
    case "streak":
      return { emoji: "🔥", bg: "bg-app-info/10", border: "border-l-app-info", text: "text-app-info" };
    case "session":
      return { emoji: "📅", bg: "bg-app-accent/10", border: "border-l-app-accent", text: "text-app-accent" };
    default:
      return { emoji: "📝", bg: "bg-app-surface-raised", border: "border-l-app-border", text: "text-app-text-muted" };
  }
}

function WelcomeCard() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-md w-full overflow-hidden border-0 shadow-lg py-0">
        <div className="bg-linear-to-br from-app-primary/20 via-app-accent/10 to-app-success/10 p-8 md:p-12 text-center">
          <div className="text-7xl mb-4 animate-bounce">👑</div>
          <h1 className="text-2xl md:text-3xl font-bold text-app-text mb-2">Welcome back!</h1>
          <p className="text-app-text-muted text-base">
            Select a partner from the sidebar to view their dashboard
          </p>
          <div className="flex justify-center gap-3 mt-6 text-3xl">
            <span className="animate-pulse delay-100">✨</span>
            <span className="animate-pulse delay-300">💜</span>
            <span className="animate-pulse delay-500">✨</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function AdminDashboard() {
  const { jwt, clientId } = useParams();
  const navigate = useNavigate();

  if (!clientId) {
    return <WelcomeCard />;
  }

  const { getHeaders } = useAuth();

  const { data, loading, error, refetch } = useApi<{
    points: number;
    history: HistoryItem[];
    holiday?: {
      active: boolean;
      endsAt: string | null;
      startedBy: string | null;
      adminHoliday: boolean;
      adminEndsAt?: string | null;
      request?: { starts_at: string; ends_at: string; status: string; requested_at: string } | null;
    };
  }>(`/api/admin/${jwt}/dashboard/${clientId}`);

  const [expandedBlocks, setExpandedBlocks] = useState<Set<number>>(new Set());

  const toggleExpand = (index: number) => {
    setExpandedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const [requestLoading, setRequestLoading] = useState(false);

  const handleHolidayRequest = async (action: "approve" | "deny") => {
    setRequestLoading(true);
    try {
      const res = await fetch(`/api/admin/${jwt}/clients/${clientId}/holiday-request`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        showToast.success(action === "approve" ? "Holiday request approved" : "Holiday request denied");
        refetch();
      } else {
        const d = await res.json().catch(() => ({}));
        showToast.error(d.error || "Failed to process request");
      }
    } catch {
      showToast.error("Failed to process request");
    } finally {
      setRequestLoading(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  const points = data?.points ?? 0;
  const history: HistoryItem[] = data?.history ?? [];

  const ruleBreaks = history.filter((h) => h.type === "rule_broken").slice(0, 5);
  const choreEvents = history.filter((h) => h.type === "chore_completed" || h.type === "chore_overdue").slice(0, 5);
  const allActivity = history.slice(0, 10);

  const quickActions = [
    { label: "Chores", icon: ListChecks, path: "chores", bg: "bg-app-success/10", color: "text-app-success" },
    { label: "Rules", icon: BookOpen, path: "rules", bg: "bg-app-danger/10", color: "text-app-danger" },
    { label: "Rewards", icon: Gift, path: "rewards", bg: "bg-app-info/10", color: "text-app-info" },
    { label: "Punishments", icon: Skull, path: "punishments", bg: "bg-app-warning/10", color: "text-app-warning" },
    { label: "Sessions", icon: CalendarDays, path: "sessions", bg: "bg-app-accent/10", color: "text-app-accent" },
    { label: "Messages", icon: MessageCircle, path: "messages", bg: "bg-app-primary/10", color: "text-app-primary" },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Holiday Banner */}
      {data?.holiday?.active && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 py-0">
          <CardContent className="flex items-center gap-3 p-4">
            <Palmtree className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">
                🏖️ Holiday mode is active
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Chores, rules, and notifications are paused.
                {(data.holiday.adminHoliday && data.holiday.adminEndsAt) && (
                  <> Ends {new Date(data.holiday.adminEndsAt).toLocaleDateString()}</>
                )}
                {(!data.holiday.adminHoliday && data.holiday.endsAt) && (
                  <> Ends {new Date(data.holiday.endsAt).toLocaleDateString()} (partner holiday)</>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Holiday Request */}
      {data?.holiday?.request?.status === "pending" && !data?.holiday?.active && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 py-0">
          <CardContent className="flex items-center justify-between gap-3 p-4">
            <div className="flex items-center gap-3 min-w-0">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  🏖️ Holiday request
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  {new Date(data.holiday.request.starts_at).toLocaleDateString()} → {new Date(data.holiday.request.ends_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                disabled={requestLoading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleHolidayRequest("approve")}
              >
                <Check className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={requestLoading}
                className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30"
                onClick={() => handleHolidayRequest("deny")}
              >
                <X className="w-4 h-4 mr-1" /> Deny
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Row: Points + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Points Card */}
        <Card className="border-0 shadow-lg overflow-hidden py-0">
          <div className="bg-linear-to-br from-app-primary/20 via-app-accent/10 to-transparent p-6 flex flex-col items-center justify-center text-center">
            <p className="text-sm font-medium text-app-text-muted mb-3">Current Points</p>
            <div className={`w-24 h-24 rounded-full flex items-center justify-center shadow-lg ${points >= 0 ? "bg-app-success/20" : "bg-app-danger/20"}`}>
              <span className={`text-4xl font-extrabold ${points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                {points}
              </span>
            </div>
            <div className="flex gap-1 mt-3 text-lg">
              <span>✨</span><span>💎</span><span>✨</span>
            </div>
          </div>
        </Card>

        {/* Quick Actions Grid */}
        <div className="lg:col-span-2 grid grid-cols-3 sm:grid-cols-3 gap-2 md:gap-3">
          {quickActions.map((action) => (
            <Card
              key={action.path}
              className="cursor-pointer border-0 shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
              onClick={() => navigate(`/admin/${jwt}/${clientId}/${action.path}`)}
            >
              <CardContent className="p-3 md:p-4 flex flex-col items-center text-center gap-2">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl ${action.bg} flex items-center justify-center`}>
                  <action.icon className={`w-5 h-5 md:w-6 md:h-6 ${action.color}`} />
                </div>
                <span className="text-xs md:text-sm font-medium text-app-text">{action.label}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Points Chart */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-base text-app-text-muted">📈 Points Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <PointsChart history={history} height={200} />
        </CardContent>
      </Card>

      {/* Side-by-side: Rule Breaks + Chore Events */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Recent Rule Breaks */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-base text-app-danger flex items-center gap-2">
              ⚠️ Recent Rule Breaks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ruleBreaks.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-3xl mb-2">😇</div>
                <p className="text-sm text-app-text-muted">No rule breaks — good behavior!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {ruleBreaks.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-app-danger/10 hover:bg-app-danger/15 transition-colors">
                    <span className="text-lg shrink-0">⚠️</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-app-text truncate">{h.entity_name || "Rule broken"}</p>
                      <p className="text-xs text-app-text-muted">{new Date(h.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="text-sm font-bold text-app-danger shrink-0">{h.points}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Chore Events */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-base text-app-success flex items-center gap-2">
              📋 Recent Chore Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            {choreEvents.length === 0 ? (
              <div className="text-center py-4">
                <div className="text-3xl mb-2">🧹</div>
                <p className="text-sm text-app-text-muted">No chore events yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {choreEvents.map((h, i) => {
                  const isOverdue = h.type === "chore_overdue";
                  return (
                    <div key={i} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${isOverdue ? "bg-app-warning/10 hover:bg-app-warning/15" : "bg-app-success/10 hover:bg-app-success/15"}`}>
                      <span className="text-lg shrink-0">{isOverdue ? "⏰" : "✅"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-app-text truncate">{h.entity_name || (isOverdue ? "Overdue" : "Completed")}</p>
                        <p className="text-xs text-app-text-muted">{new Date(h.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-sm font-bold shrink-0 ${isOverdue ? "text-app-danger" : "text-app-success"}`}>
                        {h.points >= 0 ? "+" : ""}{h.points}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Activity Feed */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base text-app-text-muted">🕐 All Activity</CardTitle>
          <Button variant="ghost" size="sm" className="text-xs text-app-text-muted gap-1" onClick={() => navigate(`/admin/${jwt}/${clientId}/activity`)}>
            <History className="w-3.5 h-3.5" /> View All
          </Button>
        </CardHeader>
        <CardContent>
          {allActivity.length === 0 ? (
            <div className="text-center py-6">
              <div className="text-4xl mb-2">📭</div>
              <p className="text-sm text-app-text-muted">No activity yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allActivity.map((h, i) => {
                const type = getEventType(h.type);
                const style = getEventStyle(type);
                const isBatch = h.type === "session_purchase" || h.type === "session_purchase_reversed";
                const isExpanded = expandedBlocks.has(i);
                const label = isBatch
                  ? `${h.type === "session_purchase" ? "Session purchase" : "Purchase reversed"} (${h.items?.length || 0} items)`
                  : (h.entity_name || h.type?.replace(/_/g, " ") || "Event");
                return (
                  <div key={i}>
                    <div
                      className={`flex items-center gap-3 p-2.5 rounded-lg border-l-4 ${style.border} ${style.bg} hover:opacity-90 transition-opacity ${isBatch && h.items?.length ? "cursor-pointer" : ""}`}
                      onClick={() => isBatch && h.items?.length && toggleExpand(i)}
                    >
                      {isBatch && h.items?.length ? (
                        <span className="text-lg shrink-0">
                          {isExpanded ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />}
                        </span>
                      ) : (
                        <span className="text-lg shrink-0">{style.emoji}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-app-text truncate">{label}</p>
                        <p className="text-xs text-app-text-muted">
                          {new Date(h.created_at).toLocaleDateString()} · {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <span className={`text-sm font-bold shrink-0 ${h.points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                        {h.points >= 0 ? "+" : ""}{h.points}
                      </span>
                    </div>
                    {isBatch && isExpanded && h.items && h.items.length > 0 && (
                      <div className="ml-6 mt-1 space-y-1">
                        {h.items.map((item, j) => (
                          <div key={j} className="flex items-center gap-2 p-1.5 rounded-md bg-app-surface-raised/50 text-xs">
                            <span className="text-base shrink-0">{item.icon}</span>
                            <span className="flex-1 min-w-0 truncate text-app-text">{item.name || "[Encrypted]"}</span>
                            <span className="text-app-text-muted">{item.item_type.replace(/_/g, " ")}</span>
                            <span className={`font-semibold shrink-0 ${item.points >= 0 ? "text-app-success" : "text-app-danger"}`}>
                              {item.points >= 0 ? "+" : ""}{item.points}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
